import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format number with Swiss-style thousands separator (apostrophe)
export function formatNumber(num, decimals = 0) {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const parts = Number(num).toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return parts.join('.');
}

// Format percentage
export function formatPercent(num) {
  if (num === null || num === undefined || isNaN(num)) return '0%';
  return `${Number(num).toFixed(1)}%`;
}

// Format currency value
export function formatCurrency(value, currency = 'CHF') {
  const formatted = formatNumber(value, 0);
  return currency === 'CHF' ? formatted : `${formatted} ${currency}`;
}

// Get color class for margin percentage (respects user's target)
export function getMarginColor(pct, marginTarget = 30) {
  if (pct >= marginTarget) return 'text-green-600';
  if (pct >= 15) return 'text-yellow-600';
  return 'text-red-600';
}

// Get color class for gap (positive = over budget)
export function getGapColor(gap) {
  if (gap > 0) return 'text-red-600';
  if (gap < 0) return 'text-green-600';
  return 'text-gray-600';
}

// Format gap with sign
export function formatGap(gap) {
  const sign = gap > 0 ? '+' : '';
  return `${sign}${formatNumber(gap, 0)}`;
}

// Convert base currency amount to presentation currency using FX rates
// Named chfToPresentation for backward compatibility — "chf" means "core currency amount"
export function chfToPresentation(baseAmount, targetCurrency, fxRates, baseCurrency = 'CHF') {
  if (!fxRates || targetCurrency === baseCurrency) return baseAmount;
  return fromBase(baseAmount, baseCurrency, targetCurrency, fxRates);
}

// Convert any currency amount to CHF using FX rates
export function toChf(amount, currency, fxRates) {
  if (!fxRates || currency === 'CHF') return amount;
  if (currency === 'EUR') return amount * (fxRates.EUR_CHF || 1);
  if (currency === 'USD') return amount * (fxRates.USD_CHF || 1);
  return amount;
}

// Convert CHF amount to any target currency
export function fromChf(chfAmount, target, fxRates) {
  if (!fxRates || target === 'CHF') return chfAmount;
  if (target === 'EUR') return chfAmount / (fxRates.EUR_CHF || 1);
  if (target === 'USD') return chfAmount / (fxRates.USD_CHF || 1);
  return chfAmount;
}

// Convert any currency amount to any base currency (route: source -> CHF -> base)
export function toBase(amount, currency, base, fxRates) {
  const chf = toChf(amount, currency, fxRates);
  return fromChf(chf, base, fxRates);
}

// Convert base currency amount to any target currency (route: base -> CHF -> target)
export function fromBase(baseAmount, base, target, fxRates) {
  const chf = toChf(baseAmount, base, fxRates);
  return fromChf(chf, target, fxRates);
}
